import type { Competitor } from '../types'

// Sources (checked 2026-10-01): help.stan.store/article/31-creator-vs-creator-pro
// (official plan comparison; stan.store/pricing returns 404), stan.store/blog/
// stan-store-pricing, help.stan.store articles on fees, design, custom domains,
// branding, analytics, and the mobile app.
const stan: Competitor = {
  slug: 'stan',
  aliases: ['stan-store'],
  name: 'Stan',
  tagline: 'Stan Store: a creator storefront for digital products, courses, and coaching.',
  site: 'https://stan.store',
  pricingUrl: 'https://help.stan.store/article/31-creator-vs-creator-pro',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Stan Store alternative: mychar.gg vs Stan compared (2026)',
    description: 'A Stan Store alternative for a designed link in bio instead of a storefront. Free plan, one-time upgrades, effects, music, and clips compared.'
  },
  hero: {
    title: 'The Stan Store alternative for a link in bio that looks like you',
    description: 'Stan is a storefront. mychar.gg is a link-in-bio page you design yourself, live, with effects, music, and clips, and it is free to start.'
  },
  summary: 'mychar.gg is a Stan Store alternative for anyone whose bio link is about who they are rather than checkout: it has a free plan, one-time upgrades from €6.99, deep design control, a music player, video clips, and a custom domain option. Stan has no free plan and starts at $25 a month billed yearly, but it is the stronger choice if you sell courses, coaching, or digital products.',
  chooseMychar: [
    'want a free page instead of a $25 a month store',
    'want the page to look like you, with effects, fonts, and your own background',
    'want music, video clips, and live Twitch or YouTube on the page',
    'want your own domain, which Stan does not offer'
  ],
  chooseThem: [
    'sell digital products, courses, coaching, or memberships',
    'want checkout, upsells, and email marketing in one place',
    'want Instagram auto-DMs that deliver products'
  ],
  features: {
    free_plan: { yes: false, text: 'No, 14-day trial' },
    paid_plans: 'Creator $25, Creator Pro $79 a month',
    billing: 'Monthly or yearly subscription',
    first_year: '$300 (Creator, billed yearly)',
    custom_domain: { yes: false, text: 'Not offered' },
    remove_branding: { yes: true, text: 'Creator Pro, $79 a month' },
    editor: 'Dashboard forms with a live preview',
    drag_drop: { yes: true, text: 'Reorder products and sections' },
    themes: { yes: true, text: '11 store templates' },
    fonts: 'A preset list',
    custom_code: { yes: false, text: 'Not supported' },
    links: { yes: true, text: 'Link and media cards' },
    video_clips: 'In product pages and courses',
    comments: { yes: false, text: 'Reviews on checkout pages' },
    youtube: 'Video embed cards',
    analytics_free: { yes: false, text: 'No free plan' },
    analytics_live: 'Store analytics, pixels on Pro',
    store: { yes: true, text: 'Products, courses, bookings, 0% Stan fee' },
    email_capture: { yes: true, text: 'Lead magnets, email sends on Pro' },
    mobile_app: { yes: true, text: 'iPhone and iPad' }
  },
  plans: [
    { name: 'Creator', price: '$25', period: '/month, billed yearly', note: 'Or $29 monthly. Storefront, products, courses, coaching, lead magnets, Instagram auto-DM.' },
    { name: 'Creator Pro', price: '$79', period: '/month, billed yearly', note: 'Or $99 monthly. Upsells, funnels, payment plans, email marketing, pixels, no branding.' }
  ],
  plansNote: 'USD. No free plan; a 14-day trial is available. Stan takes no platform fee, but payment processor fees apply.',
  advantages: [
    {
      title: 'A free plan',
      description: 'Your page, unlimited platform links, a music player, and analytics cost nothing. Stan starts at $25 a month billed yearly.',
      icon: 'i-lucide-gift'
    },
    {
      title: 'Design you control',
      description: 'Layouts, fonts, video backgrounds, animated effects, and custom cursors. Stan stores share 11 templates and do not allow custom code.',
      icon: 'i-lucide-palette'
    },
    {
      title: 'Your own domain',
      description: 'The custom domain add-on is €2.99 per month on any plan. Stan stores stay on stan.store.',
      icon: 'i-lucide-globe'
    },
    {
      title: 'Music and live sections',
      description: 'A music player on every plan, video clips that play in place, and YouTube and Twitch sections that update themselves.',
      icon: 'i-lucide-music'
    },
    {
      title: 'One-time upgrades',
      description: 'Pro is €6.99 once and Elite €12.99 once. No monthly bill for your link in bio.',
      icon: 'i-lucide-badge-euro'
    },
    {
      title: 'Community on the page',
      description: 'Visitor comments, a Discord section, linked friends, and achievements.',
      icon: 'i-lucide-users'
    }
  ],
  strengths: [
    {
      title: 'Selling is the product',
      description: 'Stan checks out digital products, courses, coaching calls, memberships, and communities directly from your bio link, with no Stan platform fee.'
    },
    {
      title: 'Conversion tools',
      description: 'Creator Pro adds order bumps, upsell funnels, discount codes, and payment plans such as Klarna and Afterpay.'
    },
    {
      title: 'Email marketing',
      description: 'Lead magnets collect emails on Creator, and Creator Pro sends broadcasts and automated flows.'
    },
    {
      title: 'Instagram auto-DM',
      description: 'Stan can send a product or link to anyone who comments a keyword on your post.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Stan Store alternative',
      paragraphs: [
        'Stan is built for creators who sell: courses, coaching, digital downloads, and memberships, checked out from a link in bio. For that job it is very good. For a creator who mostly wants one link that shows their work, it is an expensive starting point, at $25 a month billed yearly with no free plan, and the stores tend to look alike because there are 11 templates and no custom code.',
        'Two things come up often. Stan does not offer custom domains on any plan, and removing the Stan branding takes Creator Pro at $79 a month billed yearly.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg is a link-in-bio page you design, not a store. In the [editor](/docs/customization) you work on the page itself: edit text in place, drag sections and widgets where you want them, and pick layouts, fonts, colors, backgrounds, animated effects, and cursors while you watch the result. Music, video clips, and YouTube, Twitch, and Discord sections put your content on the page.',
        'It costs far less because it does less on the selling side. The Free plan stays free, Pro is €6.99 once, and Elite €12.99 once. If you sell, you can still link to a store from your mychar.gg page, including a Stan store. The [guide for creators](/for/creators) shows how other creators set their pages up.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup). There is no trial clock to watch.' },
    { title: 'Add your links', description: 'Add your platforms from the built-in list, unlimited on every plan. Product and store links are custom links, available on Pro and Elite.' },
    { title: 'Design the page', description: 'Pick a layout, fonts, colors, and a background, or start from a community [skin](/skins).' },
    { title: 'Put your content on it', description: 'Upload a music track and video clips, and connect YouTube, Twitch, or Discord.' },
    { title: 'Decide where selling lives', description: 'If you still sell digital products, keep a store and link it from your page. Then swap the link in your bios.' }
  ],
  faq: [
    {
      label: 'Can I sell courses or products on mychar.gg?',
      content: 'No. mychar.gg has no checkout. You can link to a store, Ko-fi, Patreon, or PayPal, but if you sell courses or coaching, Stan or another commerce tool fits better.'
    },
    {
      label: 'Is there a free plan, unlike Stan?',
      content: 'Yes. mychar.gg Free is free forever with no card. Stan has no free plan and starts at $25 a month billed yearly, after a 14-day trial.'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes. The custom domain add-on is €2.99 per month on any plan, including Free. Stan does not offer custom domains. See the [custom domain guide](/docs/profile/custom-domain).'
    },
    {
      label: 'Can I use mychar.gg and Stan together?',
      content: 'Yes. You can keep a store for selling and use your link in bio for everything else. Add your Stan store as a custom link on your mychar.gg page.'
    }
  ]
}

export default stan
